/** Mirrors `cyclePricing` in the API (portal-billing.service.ts); the server re-derives every charged amount. */
export type PortalCycle = 'monthly' | 'quarterly' | 'weekly';

export const PORTAL_CYCLES: PortalCycle[] = ['monthly', 'quarterly', 'weekly'];

export interface CyclePlan {
  originalPrice: number;
  discountPrice: number;
  dailyPrice: number;
  mockLimit?: number;
  analysisLimit?: number;
  quarterlyPrice?: number;
  weeklyPrice?: number;
  weeklyAnalysisLimit?: number;
}

export interface CycleOffer {
  /** Charged, in so'm. */
  price: number;
  /** Crossed out when higher than `price`. */
  originalPrice: number;
  perDay: number;
  mockLimit?: number;
  analysisLimit: number;
}

const DAYS: Record<PortalCycle, number> = { monthly: 30, quarterly: 90, weekly: 7 };

/** Tab text, and "1 oyga" style period text. */
export const CYCLE_LABELS: Record<PortalCycle, { tab: string; period: string; adjective: string }> = {
  monthly: { tab: '1 oy', period: '1 oyga', adjective: '1 oylik' },
  quarterly: { tab: '3 oy', period: '3 oyga', adjective: '3 oylik' },
  weekly: { tab: '7 kun', period: '7 kunga', adjective: '7 kunlik' },
};

/** What a plan costs and gives for a period, or null when it is not sold for that period. */
export function cycleOffer(plan: CyclePlan, cycle: PortalCycle): CycleOffer | null {
  const analysisLimit = plan.analysisLimit ?? 0;
  if (cycle === 'monthly') {
    return { price: plan.discountPrice, originalPrice: plan.originalPrice, perDay: plan.dailyPrice, mockLimit: plan.mockLimit, analysisLimit };
  }
  if (cycle === 'quarterly') {
    const price = plan.quarterlyPrice ?? 0;
    if (price <= 0) return null;
    return {
      price,
      originalPrice: Math.max(price, plan.discountPrice * 3),
      perDay: Math.round(price / DAYS.quarterly),
      mockLimit: plan.mockLimit && plan.mockLimit * 3,
      analysisLimit: analysisLimit * 3,
    };
  }
  const price = plan.weeklyPrice ?? 0;
  if (price <= 0) return null;
  return {
    price,
    originalPrice: price,
    perDay: Math.round(price / DAYS.weekly),
    mockLimit: plan.mockLimit && Math.ceil(plan.mockLimit / 4),
    analysisLimit: plan.weeklyAnalysisLimit ?? 0,
  };
}

/** The periods at least one of `plans` is sold for, in display order. */
export function offeredCycles(plans: CyclePlan[]): PortalCycle[] {
  return PORTAL_CYCLES.filter((cycle) => plans.some((plan) => cycleOffer(plan, cycle) !== null));
}
